/**
 * Sorting and showing a form's dates, either of which may be absent.
 *
 * Both default to null in the model, and the GraphQL schema used to declare
 * them non-null — so an undated form failed the whole `forms` query and the
 * list rendered empty. With that corrected, a null actually reaches here.
 *
 * The comparisons below used to read `.seconds` off what is an ISO **string**,
 * so every one of them was `undefined - undefined`, i.e. `NaN`, and sorted
 * nothing at all. A real null would have thrown on top of that.
 */

/** Milliseconds, or null for absent and unparseable alike. */
export const dateValue = (value: unknown): number | null => {
  if (!value) return null;
  const time = new Date(value as string).getTime();
  return isNaN(time) ? null : time;
};

/**
 * Undated forms sort last in **both** directions.
 *
 * Treating a missing date as zero would file every undated form under 1970,
 * which puts them first in one direction and looks like data corruption.
 */
export const byDate =
  (key: 'openDate' | 'closeDate', direction: 'asc' | 'desc') =>
  (row1: any, row2: any): number => {
    const a = dateValue(row1?.[key]);
    const b = dateValue(row2?.[key]);
    if (a === null && b === null) return 0;
    if (a === null) return 1;
    if (b === null) return -1;
    return direction === 'asc' ? a - b : b - a;
  };
